/**
 * Synchronous input formatters for live keystroke formatting.
 *
 * Applied on every `onChange` event via the `formatter` prop on `RhfInputText`,
 * providing immediate, flicker-free masking without the one-render lag of
 * async effect-based formatting.
 *
 * ## Signature: (value, prevValue) → string
 *
 * Both the new raw value AND the previous formatted value are required to detect
 * deletion direction. This is the only reliable way to handle the "backspace
 * at boundary" trap:
 *
 *   Problem: SSN field shows "989-89-". User presses backspace.
 *   Browser sends onChange with value "989-89" (dash removed by browser).
 *   Formatter strips to 5 digits → re-inserts dash → "989-89-". User is stuck.
 *
 *   Fix: if value.length < prevValue.length, the user is deleting. At exactly-
 *   boundary digit counts (3 for SSN first group, 5 for second), skip the
 *   trailing separator so the cursor advances through the gap naturally.
 *
 * @see effectProcessor.ts — FORMAT_FNS delegates to INPUT_FORMAT_FNS for DRY.
 */

/**
 * Formats a US Social Security Number as NNN-NN-NNNN.
 *
 * Boundaries: 3 digits → "NNN-", 5 digits → "NNN-NN-" (typing forward).
 * When deleting at a boundary, the trailing dash is omitted.
 */
export function formatSsnUs(value: string, prevValue = ''): string {
  const d = value.replace(/\D/g, '').slice(0, 9);
  if (d.length === 0) return '';

  const isDeleting = value.length < prevValue.length;

  // At dash boundaries: auto-insert separator only when typing forward
  if (d.length === 3) return isDeleting ? d : `${d}-`;
  if (d.length === 5) {
    const base = `${d.slice(0, 3)}-${d.slice(3)}`;
    return isDeleting ? base : `${base}-`;
  }

  if (d.length > 5) return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
  if (d.length > 3) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return d;
}

/**
 * Formats a US phone number as NNN-NNN-NNNN.
 *
 * Boundaries: 3 digits → "NNN-", 6 digits → "NNN-NNN-" (typing forward).
 * When deleting at a boundary, the trailing dash is omitted.
 */
export function formatPhoneUs(value: string, prevValue = ''): string {
  const d = value.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';

  const isDeleting = value.length < prevValue.length;

  if (d.length === 3) return isDeleting ? d : `${d}-`;
  if (d.length === 6) {
    const base = `${d.slice(0, 3)}-${d.slice(3)}`;
    return isDeleting ? base : `${base}-`;
  }

  if (d.length > 6) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return d;
}

/**
 * Formats a US Federal Tax ID (EIN) as NN-NNNNNNN.
 *
 * Boundary: 2 digits → "NN-" (typing forward).
 * When deleting at the boundary, the trailing dash is omitted.
 */
export function formatTaxIdUs(value: string, prevValue = ''): string {
  const d = value.replace(/\D/g, '').slice(0, 9);
  if (d.length === 0) return '';

  const isDeleting = value.length < prevValue.length;

  if (d.length === 2) return isDeleting ? d : `${d}-`;

  return d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d;
}

/**
 * Strips all non-digit characters, keeping only 0-9.
 * Used for non-US phone numbers where only digits are allowed.
 */
export function formatDigitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/** Registry of all synchronous input formatters keyed by FORMAT_FN name. */
export const INPUT_FORMAT_FNS: Record<string, (value: string, prevValue?: string) => string> = {
  SSN_US: formatSsnUs,
  PHONE_US: formatPhoneUs,
  TAX_ID: formatTaxIdUs,
  DIGITS_ONLY: formatDigitsOnly,
};
